<template>
  <div
    ref="root"
    class="card border mb-3"
    :class="{ 'delivery-card--highlighted': highlighted }"
  >
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <div>
        <div class="fw-semibold">
          {{ windowText }}
          <span
            class="fs-10 fw-normal text-body-tertiary ms-1"
            :title="$t('forwards.deals.plan.delivery_id', { id: delivery.id })"
            data-testid="delivery-id"
            >#{{ delivery.id }}</span
          >
        </div>
        <div class="text-body-tertiary fs-10">{{ priceText }}</div>
      </div>

      <div class="d-flex align-items-center gap-2 flex-wrap">
        <button
          v-if="!isVoided"
          type="button"
          class="btn btn-sm btn-ghost"
          @click="emit('producer-prices', delivery.id)"
        >
          {{ $t("forwards.deals.producer_prices.button") }}
        </button>
        <button
          v-if="managesDeal"
          type="button"
          class="btn btn-sm btn-ghost"
          data-testid="delivery-notes-button"
          @click="emit('delivery-notes', delivery.id)"
        >
          {{ $t("forwards.deals.delivery_notes.button") }}
        </button>
        <RouterLink
          v-if="delivery.transaction_status !== null"
          :to="`/admin/transactions/${delivery.sales_transaction_id}/edit`"
          class="btn btn-sm btn-ghost"
          data-testid="delivery-transaction-link"
        >
          {{
            $t("forwards.deals.plan.transaction_link", {
              id: delivery.sales_transaction_id,
            })
          }}
        </RouterLink>
        <span v-else class="badge rounded-pill" :class="statusClass">
          {{ statusLabel }}
        </span>
        <template v-if="managesDeal">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="!delivery.ship_gate.ok"
            @click="onDeliverClicked"
          >
            {{ $t("forwards.deals.plan.deliver") }}
          </button>
          <span
            v-if="!delivery.ship_gate.ok && shipGateHint"
            class="text-body-tertiary fs-10 deliver-hint"
            >{{ shipGateHint }}</span
          >
        </template>
      </div>
    </div>

    <div class="card-body">
      <div class="d-flex align-items-center gap-3 flex-wrap mb-2">
        <div class="fill-summary">
          <EscrowFillBarComponent
            :escrowed="delivery.escrowed_mwh"
            :total="delivery.planned_mwh"
            :showShortfall="shortfallMwh > 0"
          />
        </div>
        <span
          v-if="shortfallMwh > 0"
          class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
        >
          {{
            $t("forwards.deals.plan.delivery_shortfall", {
              mwh: formatNumberWithSpaces(shortfallMwh),
            })
          }}
        </span>
      </div>

      <div
        v-if="hasStaffDetails"
        class="mb-2"
        data-testid="delivery-staff-notes"
      >
        <p
          v-if="delivery.default_producer_price !== null"
          class="text-body-tertiary fs-10 mb-1"
        >
          {{
            $t("forwards.deals.delivery_notes.default_producer_price", {
              price: $t("forwards.deals.plan.unit_price", {
                price: delivery.default_producer_price,
              }),
            })
          }}
        </p>
        <template v-if="delivery.internal_notes !== null">
          <div class="fw-semibold fs-10">
            {{ $t("forwards.deals.delivery_notes.heading") }}
          </div>
          <p class="delivery-notes fs-10 mb-0">{{ delivery.internal_notes }}</p>
        </template>
      </div>

      <DeliveryContentsComponent
        v-if="delivery.contents"
        :contents="delivery.contents"
        class="mb-2"
        data-testid="delivery-contents"
      />

      <p
        v-for="warning in delivery.warnings"
        :key="`${warning.kind}-${warning.detail}`"
        class="advisory-note mb-2"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{ warning.detail }}</span>
      </p>

      <table v-if="delivery.slices.length > 0" class="table table-sm mb-0">
        <thead>
          <tr>
            <th scope="col">{{ $t("forwards.deals.plan.period") }}</th>
            <th scope="col">{{ $t("forwards.deals.plan.backed_by") }}</th>
            <th scope="col">{{ $t("forwards.deals.plan.escrowed_of") }}</th>
            <th v-if="canManage" scope="col" class="text-end">
              {{ $t("forwards.common.actions") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <SliceRowComponent
            v-for="slice in delivery.slices"
            :key="slice.id"
            :slice="slice"
            :canManage="canManage"
            :canMoveEscrow="canMoveEscrow"
            :disabled="busy"
            @slice-action="emit('slice-action', $event)"
          />
        </tbody>
      </table>
      <p v-else class="text-body-tertiary fs-10 mb-0">
        {{ $t("forwards.deals.plan.no_slices") }}
      </p>
    </div>

    <ConfirmationModalComponent
      :visible="showOutsideWindowConfirm"
      :title="$t('forwards.deals.plan.outside_window_title')"
      :message="outsideWindowMessage"
      :confirmButtonText="$t('forwards.deals.plan.deliver_anyway')"
      @confirm="startTransfer"
      @cancel="showOutsideWindowConfirm = false"
    />

    <TransferModalComponent
      v-if="showTransferModal"
      :visible="showTransferModal"
      :transactionId="delivery.sales_transaction_id"
      @confirm="onTransferred"
      @cancel="showTransferModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import {
  formatDateTimeIntoIsoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import DeliveryContentsComponent from "@/forwards2/deals/DeliveryContentsComponent.vue";
import SliceRowComponent from "@/forwards2/deals/SliceRowComponent.vue";
import type { SliceAction } from "@/forwards2/deals/sliceActions";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import type { DeliveryResponse } from "@/forwards2/forwardsTypes";
import TransferModalComponent from "@/money/admin/transactions/TransferModalComponent.vue";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * One shipment of the plan: when it must go out, what it is worth, what stands behind it.
 *
 * The ship gate is the backend's, not a local re-derivation — its reasons are English admin
 * sentences and are only rendered for admins, who are also the only people who can deliver.
 * Delivering outside the window is allowed but confirmed first, since the window is a promise to
 * the buyer rather than a technical bound.
 */
const props = withDefaults(
  defineProps<{
    delivery: DeliveryResponse;
    /** Delivering and the delivery's notes, which stay open on a deal whose commitments are frozen. */
    managesDeal: boolean;
    canManage: boolean;
    /** Registry-backed escrow moves, which only a super admin may run. */
    canMoveEscrow?: boolean;
    /** Committed-but-not-escrowed MWh for this delivery, from `DealFlags`. */
    shortfallMwh?: number;
    highlighted?: boolean;
    /**
     * Scroll a highlighted card into view. The page clears it once the card reports `scrolled`, so a
     * remount (the plan editor closing) or a refresh does not scroll again.
     */
    scrollWhenHighlighted?: boolean;
    busy?: boolean;
  }>(),
  {
    canMoveEscrow: false,
    shortfallMwh: 0,
    highlighted: false,
    scrollWhenHighlighted: false,
    busy: false,
  }
);

const emit = defineEmits<{
  "slice-action": [action: SliceAction];
  delivered: [deliveryId: number];
  "producer-prices": [deliveryId: number];
  "delivery-notes": [deliveryId: number];
  scrolled: [deliveryId: number];
}>();

const { t } = useI18n();
const root = useTemplateRef<HTMLElement>("root");
const deliveryWindowText = useDeliveryWindowText();

const showOutsideWindowConfirm = ref(false);
const showTransferModal = ref(false);

const windowText = computed(() => deliveryWindowText(props.delivery));

const priceText = computed(() =>
  props.delivery.unit_price === null
    ? t("forwards.deals.plan.no_price")
    : t("forwards.deals.plan.unit_price", { price: props.delivery.unit_price })
);

const statusLabel = computed(() =>
  props.delivery.transaction_status === null
    ? t("forwards.deals.transaction_status.voided")
    : t(
        `forwards.deals.transaction_status.${props.delivery.transaction_status}`
      )
);

const statusClass = computed(() => {
  switch (props.delivery.transaction_status) {
    case "COMPLETED":
      return "bg-success-subtle text-success-emphasis";
    case "DELIVERED":
      return "bg-info-subtle text-info-emphasis";
    case "READY_TO_TRANSFER":
      return "bg-primary-subtle text-primary-emphasis";
    case null:
      return "bg-danger-subtle text-danger-emphasis";
    default:
      return "bg-secondary-subtle text-secondary-emphasis";
  }
});

const hasStaffDetails = computed(
  () =>
    props.delivery.internal_notes !== null ||
    props.delivery.default_producer_price !== null
);

/** The backend's own reasons, joined — English, admin-only (WP-04a C-04a-10). */
const shipGateHint = computed(() => props.delivery.ship_gate.reasons.join(" "));

/** A voided delivery invoiced nothing and its producer lines are gone, so there are no prices to show. */
const isVoided = computed(() => props.delivery.transaction_status === null);

const isOutsideWindow = computed(() => {
  const today = formatDateTimeIntoIsoDate(new Date());
  const start = props.delivery.delivery_window_start;
  return (
    today > props.delivery.delivery_window_end ||
    (start !== null && today < start)
  );
});

const outsideWindowMessage = computed(() =>
  t("forwards.deals.plan.outside_window_body", { window: windowText.value })
);

const scrollsIntoView = computed(
  () => props.highlighted && props.scrollWhenHighlighted
);

onMounted(() => scrollIntoViewIfAsked());

watch(scrollsIntoView, () => scrollIntoViewIfAsked());

const scrollIntoViewIfAsked = () => {
  if (!scrollsIntoView.value) return;
  root.value?.scrollIntoView({ block: "center" });
  emit("scrolled", props.delivery.id);
};

const onDeliverClicked = () => {
  if (isOutsideWindow.value) {
    showOutsideWindowConfirm.value = true;
    return;
  }
  startTransfer();
};

const startTransfer = () => {
  showOutsideWindowConfirm.value = false;
  showTransferModal.value = true;
};

const onTransferred = () => {
  showTransferModal.value = false;
  emit("delivered", props.delivery.id);
};
</script>

<style scoped lang="scss">
.delivery-card--highlighted {
  border-color: var(--phoenix-primary);
  box-shadow: 0 0 0 0.15rem var(--phoenix-primary-bg-subtle);
}

.fill-summary {
  min-width: 16rem;
  flex: 1 1 16rem;
}

.deliver-hint {
  max-width: 16rem;
}

.delivery-notes {
  white-space: pre-wrap;
}
</style>
